import {
  ChangeDetectorRef,
  Component,
  HostListener,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { NgSelectComponent } from '@ng-select/ng-select';
import { map, take, tap } from 'rxjs/operators';
import {
  PATH_BRANCH,
  PATH_BUILDER,
  PATH_ENV,
  PATH_FILE,
  PATH_ORG,
  PATH_PROJECT,
  PATH_REPO,
  PATH_REPORTS,
  PATH_SELECT_FILE,
  PROJECT_ENV_PROD
} from '#common/constants/top';
import { RepoTypeEnum } from '#common/enums/repo-type.enum';
import { ToBackendRequestInfoNameEnum } from '#common/enums/to/to-backend-request-info-name.enum';
import type { EnvsItem } from '#common/zod/backend/envs-item';
import type {
  ToBackendGetEnvsListRequestPayload,
  ToBackendGetEnvsListResponse
} from '#common/zod/to-backend/envs/to-backend-get-envs-list';
import { checkNavMain } from '#front/app/functions/check-nav-main';
import { FileQuery } from '#front/app/queries/file.query';
import { NavQuery, NavState } from '#front/app/queries/nav.query';
import { SessionBundleQuery } from '#front/app/queries/session-bundle.query';
import { SessionEventsQuery } from '#front/app/queries/session-events.query';
import { UiQuery } from '#front/app/queries/ui.query';
import { UserQuery, UserState } from '#front/app/queries/user.query';
import { ApiService } from '#front/app/services/api.service';

@Component({
  standalone: false,
  selector: 'm-env-select',
  templateUrl: './env-select.component.html'
})
export class EnvSelectComponent {
  @ViewChild('envSelect', { static: false })
  envSelectElement: NgSelectComponent;

  @HostListener('window:keyup.esc')
  onEscKeyUp() {
    this.envSelectElement?.close();
  }

  repoTypeEnum = RepoTypeEnum;

  projectEnvProd = PROJECT_ENV_PROD;

  envsList: EnvsItem[] = [];
  envsListLoading = false;
  envsListLength = 0;

  selectedProjectId: string;
  selectedEnvId: string;

  nav: NavState;
  nav$ = this.navQuery.select().pipe(
    tap(x => {
      this.nav = x;

      let envsItem: EnvsItem = {
        envId: x.envId,
        projectId: x.projectId
      };
      let alreadyPresent = this.envsList.some(
        item => item.envId === envsItem.envId
      );
      this.envsList = alreadyPresent ? this.envsList : [envsItem];
      this.selectedProjectId = x.projectId;
      this.selectedEnvId = x.envId;

      this.cd.detectChanges();
    })
  );

  needSave = false;
  needSave$ = this.uiQuery.needSave$.pipe(tap(x => (this.needSave = x)));

  user: UserState;
  user$ = this.userQuery.select().pipe(
    tap(x => {
      this.user = x;
      this.cd.detectChanges();
    })
  );

  constructor(
    private uiQuery: UiQuery,
    private userQuery: UserQuery,
    private navQuery: NavQuery,
    private fileQuery: FileQuery,
    private sessionBundleQuery: SessionBundleQuery,
    private sessionEventsQuery: SessionEventsQuery,
    private apiService: ApiService,
    private cd: ChangeDetectorRef,
    private router: Router
  ) {}

  openEnvSelect() {
    this.envsListLoading = true;

    let payload: ToBackendGetEnvsListRequestPayload = {
      projectId: this.selectedProjectId,
      isFilter: true
    };

    this.apiService
      .req({
        pathInfoName: ToBackendRequestInfoNameEnum.ToBackendGetEnvsList,
        payload: payload
      })
      .pipe(
        map((resp: ToBackendGetEnvsListResponse) => resp.payload.envsList),
        tap(x => {
          this.envsList = x;
          this.envsListLoading = false;
          this.envsListLength = x.length;
          this.cd.detectChanges();
        }),
        take(1)
      )
      .subscribe();
  }

  envChange() {
    let urlTree = this.router.parseUrl(this.router.url);
    let queryParams = urlTree.queryParams;
    let urlParts = this.router.url.split('?')[0].split('/');

    let baseNavArray = [
      PATH_ORG,
      this.nav.orgId,
      PATH_PROJECT,
      this.nav.projectId,
      PATH_REPO,
      this.nav.repoId,
      PATH_BRANCH,
      this.nav.branchId,
      PATH_ENV,
      this.selectedEnvId
    ];

    if (urlParts[11] === PATH_REPORTS) {
      let uiState = this.uiQuery.getValue();
      uiState.gridApi?.deselectAll();
    }

    this.sessionBundleQuery.reset();
    this.sessionEventsQuery.reset();

    if (
      this.nav.repoType === RepoTypeEnum.Session &&
      urlParts[12] !== PATH_FILE
    ) {
      this.router.navigate([...baseNavArray, PATH_BUILDER, PATH_SELECT_FILE], {
        queryParams: queryParams
      });
      return;
    }

    let uiState = this.uiQuery.getValue();

    let pDashboardLink = uiState.projectDashboardLinks.find(
      link => link.projectId === this.nav.projectId
    );
    let pModelLink = uiState.projectModelLinks.find(
      link => link.projectId === this.nav.projectId
    );
    let pChartLink = uiState.projectChartLinks.find(
      link => link.projectId === this.nav.projectId
    );
    let pReportLink = uiState.projectReportLinks.find(
      link => link.projectId === this.nav.projectId
    );

    let navArray = checkNavMain({
      urlParts: urlParts,
      navArray: baseNavArray,
      lastDashboardId: pDashboardLink?.dashboardId,
      lastModelId: pModelLink?.modelId,
      lastChartId: pChartLink?.chartId,
      lastReportId: pReportLink?.reportId,
      fileId: this.fileQuery.getValue().fileId
    });

    this.router.navigate(navArray, { queryParams: queryParams });
  }
}
